import { useState, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Printer } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useSetlistDetail } from '@/hooks/useSetlists'
import { formatDuration, totalDurationSeconds } from '@/lib/setlistGenerator'
import { Button } from '@/components/ui/button'
import type { Setlist, Song } from '@/types'

export const PrintSetlistPage = () => {
  const { id } = useParams<{ id: string }>()
  const { sections, loading } = useSetlistDetail(id)
  const [setlist, setSetlist] = useState<Setlist | null>(null)
  const [bandName, setBandName] = useState('')

  useEffect(() => {
    if (!id) return
    supabase
      .from('setlists')
      .select('*, band:bands(name)')
      .eq('id', id)
      .single()
      .then(({ data }) => {
        if (data) {
          setSetlist(data as Setlist)
          setBandName((data as unknown as { band: { name: string } }).band?.name ?? '')
        }
      })
  }, [id])

  const getSongDuration = (song?: Song) => {
    if (!song) return 0
    return song.performance_duration_seconds ?? song.duration_seconds ?? 180
  }

  // Calculate dynamic font size per section to fit each set on one A4 page
  // A4 printable height ~257mm with 15mm margins. Header+meta ~25mm, section title ~15mm, total line ~10mm
  // Available height for songs per page: ~207mm
  const sectionFontSizes = useMemo(() => {
    const availableHeightMm = 207
    return sections.map((section) => {
      const songCount = section.items.length
      // Each song row height ≈ fontSize * 2 (line height + padding + border)
      // Max font size 1.25rem (20px), min 0.7rem (11.2px)
      const maxFontRem = 1.25
      const minFontRem = 0.7
      // At 1.25rem, row height ~2.5rem = 40px ≈ 10.6mm → fits ~19 songs
      // Calculate what font size fits all songs
      const rowHeightFactor = 2.5 // rem per row relative to font size
      const neededHeightMm = songCount * maxFontRem * rowHeightFactor * 4.233 // rem→mm (1rem≈4.233mm at 16px base)
      if (neededHeightMm <= availableHeightMm) return maxFontRem
      const scaledFont = availableHeightMm / (songCount * rowHeightFactor * 4.233)
      return Math.max(minFontRem, Math.min(maxFontRem, scaledFont))
    })
  }, [sections])

  const handlePrint = () => {
    window.print()
  }

  if (loading || !setlist) return <div className='p-6'>Loading...</div>

  return (
    <div className='print-light-page'>
      {/* Screen-only controls */}
      <div className='no-print mx-auto max-w-4xl p-6 flex gap-2'>
        <Link to={`/setlists/${id}`}>
          <Button variant='ghost' size='sm'>
            <ArrowLeft className='mr-1 h-4 w-4' /> Back to Editor
          </Button>
        </Link>
        <Button size='sm' variant='outline' onClick={handlePrint}>
          <Printer className='mr-1 h-3 w-3' /> Print
        </Button>
      </div>

      {/* Printable A4 content */}
      <div className='print-setlist mx-auto' style={{ maxWidth: '210mm', padding: '15mm' }}>
        <style>{`
          .print-light-page {
            color-scheme: light;
            --background: 0 0% 100% !important;
            --foreground: 240 10% 3.9% !important;
            --card: 0 0% 100% !important;
            --card-foreground: 240 10% 3.9% !important;
            --muted-foreground: 240 3.8% 46.1% !important;
            --border: 240 5.9% 90% !important;
            background: #fff;
            color: #111;
            min-height: 100vh;
          }
          @media print {
            @page {
              size: A4 portrait;
              margin: 15mm;
            }
            :root {
              --background: 0 0% 100% !important;
              --foreground: 240 10% 3.9% !important;
              --card: 0 0% 100% !important;
              --card-foreground: 240 10% 3.9% !important;
              --muted-foreground: 240 3.8% 46.1% !important;
              --border: 240 5.9% 90% !important;
            }
            .dark {
              --background: 0 0% 100% !important;
              --foreground: 240 10% 3.9% !important;
              --card: 0 0% 100% !important;
              --card-foreground: 240 10% 3.9% !important;
              --muted-foreground: 240 3.8% 46.1% !important;
              --border: 240 5.9% 90% !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              width: 100%;
              background: #fff !important;
              color: #111 !important;
              -webkit-text-size-adjust: 100%;
              text-size-adjust: 100%;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .no-print { display: none !important; }
            #root {
              background: #fff !important;
              color: #111 !important;
            }
            .print-setlist {
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              max-width: none !important;
              background: #fff !important;
              color: #111 !important;
            }
            .print-section {
              break-inside: avoid-page;
              page-break-inside: avoid;
            }
            .print-page-break {
              break-before: page;
              page-break-before: always;
            }
          }
          .print-setlist h1 { font-size: 1.8rem; font-weight: 800; margin-bottom: 0.15rem; color: #111; }
          .print-setlist .meta { font-size: 1rem; color: #555; margin-bottom: 1rem; }
          .print-setlist .section-title { font-weight: 700; border-bottom: 2px solid #000; padding-bottom: 0.2rem; margin-top: 1rem; margin-bottom: 0.3rem; }
          .print-setlist .song-row {
            display: grid;
            grid-template-columns: auto minmax(0, 1fr) minmax(0, 0.8fr) auto;
            column-gap: 0.5em;
            align-items: baseline;
            border-bottom: 1px solid #ddd;
          }
          .print-setlist .song-row .num { width: 1.8em; text-align: right; margin-right: 0.5em; color: #999; flex-shrink: 0; }
          .print-setlist .song-row .title { font-weight: 600; min-width: 0; white-space: normal; overflow-wrap: anywhere; }
          .print-setlist .song-row .artist { color: #666; min-width: 0; white-space: normal; overflow-wrap: anywhere; text-align: right; }
          .print-setlist .song-row .duration { color: #666; margin-left: 0.75em; white-space: nowrap; flex-shrink: 0; }
          .print-setlist .section-total { text-align: right; font-size: 0.85em; color: #666; margin-top: 0.15rem; }
          .print-setlist .grand-total { font-size: 1rem; font-weight: 700; text-align: right; margin-top: 1rem; border-top: 2px solid #000; padding-top: 0.4rem; }
        `}</style>

        {sections.map((section, sectionIdx) => {
          const sectionDuration = section.items.reduce((sum, item) => sum + getSongDuration(item.song), 0)
          const fontSize = sectionFontSizes[sectionIdx] ?? 1.25
          const isFirstSection = sectionIdx === 0
          return (
            <div key={section.id} className={`print-section${!isFirstSection ? ' print-page-break' : ''}`}>
              {/* Repeat header on each page */}
              <h1>{setlist.name}</h1>
              <div className='meta'>
                {bandName && <span>{bandName} &middot; </span>}
                {setlist.venue && <span>{setlist.venue} &middot; </span>}
                <span>{setlist.date}</span>
              </div>
              <div className='section-title' style={{ fontSize: `${Math.min(1.3, fontSize + 0.2)}rem` }}>{section.name}</div>
              {section.items.map((item, idx) => (
                <div key={item.id} className='song-row' style={{ fontSize: `${fontSize}rem`, padding: `${fontSize * 0.25}rem 0` }}>
                  <span className='num'>{idx + 1}.</span>
                  <span className='title'>{item.song?.title ?? 'Unknown'}</span>
                  <span className='artist'>{item.song?.artist}{item.song?.year ? ` (${item.song.year})` : ''}</span>
                  <span className='duration'>{formatDuration(getSongDuration(item.song))}</span>
                </div>
              ))}
              <div className='section-total'>{section.items.length} songs &middot; {formatDuration(sectionDuration)}</div>
            </div>
          )
        })}

        <div className='grand-total'>
          Total: {sections.reduce((sum, s) => sum + s.items.length, 0)} songs &middot;{' '}
          {formatDuration(totalDurationSeconds(sections.flatMap((s) => s.items.map((i) => i.song!)).filter(Boolean)))}
        </div>
      </div>
    </div>
  )
}
